import type { Board } from "../board";
import { Event } from "../events/event";
import { EventType } from "../events/eventType";
import { PRIORITY_DEFAULT, type ProcessableThing, isRemoteDoor } from "../interfaces";
import { PowerPlantKeyCard } from "../items/powerPlantKeyCard";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import type { PhysicalThing } from "../physicalThing";
import { Player } from "../player";
import { deserializeXY } from "../serialization";
import { Strings } from "../strings/strings";
import type { XY } from "../xy";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";
import { Terminal, TerminalState } from "./terminal";

const DOOR_ID = "powerPlantGate";
const PROMPT_RADIUS = 1;

export class PowerPlantGateTerminal extends Terminal implements ProcessableThing {
	static key = "PowerPlantGateTerminal";

	private used: boolean = false;

	constructor(xy: XY) {
		super(xy);
		this.state = TerminalState.INACTIVE;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, _props?: Record<string, unknown>): PhysicalThing[] {
		const terminal = new PowerPlantGateTerminal(xy);
		board.putThing(terminal);
		return [terminal];
	}

	processTurn(board: Board): void {
		if (!this.used) {
			this.state = board.gameFlags.activatedPowerPlant ? TerminalState.ACTIVE : TerminalState.INACTIVE;
			const player = board.player;
			if (player && this.xy.isInSquareRadius(player.xy, PROMPT_RADIUS)) {
				const text = Strings.get("prompt_openPowerPlantGate") ?? "prompt_openPowerPlantGate";
				board.emitPrompt(text);
			}
		}
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	override canInteract(): boolean {
		return !this.used;
	}

	activate(): void {
		if (this.used) return;
		this.state = TerminalState.ACTIVE;
	}

	override interact(player: PhysicalThing): void {
		const board = this.board!;

		if (!board.gameFlags.activatedPowerPlant) {
			board.addNonPhysicalThing(new Sound(SoundId.TERMINAL_ERROR, this.xy));
			board.addEvent(new Event(EventType.POWER_PLANT_GATE_NO_POWER));
			return;
		}

		if (!(player instanceof Player) || !player.inventory.consumeOne(PowerPlantKeyCard)) {
			board.addNonPhysicalThing(new Sound(SoundId.TERMINAL_ERROR, this.xy));
			board.addEvent(new Event(EventType.POWER_PLANT_GATE_NO_KEYCARD));
			return;
		}

		for (const thing of board.getPhysicalThings()) {
			if (isRemoteDoor(thing) && thing.doorId === DOOR_ID) {
				thing.open();
			}
		}

		this.used = true;
		this.state = TerminalState.INACTIVE;
		board.addNonPhysicalThing(new Sound(SoundId.TERMINAL_CONFIRM, this.xy));
		board.addEvent(new Event(EventType.POWER_PLANT_GATE_OPENED));
	}

	// Unused: this class overrides interact() directly to branch on power/keycard state.
	runTerminalAction(): void {}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			state: this.state,
			used: this.used,
		};
	}

	static deserialize(data: any): PowerPlantGateTerminal {
		const t = new PowerPlantGateTerminal(deserializeXY(data.xy));
		t.visualId = data.visualId;
		t.state = data.state ?? TerminalState.INACTIVE;
		t.used = data.used ?? false;
		return t;
	}
}
